import { AGORA_SIMULADO, ApiError, atrasoSimulado, fetchOrSimulate, MOCK_FORCED, rangeQuery, request, type Envelope } from '../../lib/api';
import type { PeriodRange } from '../../types';
import { comissaoSimuladaCents, simularDetalheDoLead } from '../leads/mock';
import { contatosQueContam } from '../leads/resumo';
import { CANAL } from '../leads/vocabulario';
import { simularComercial } from './mock';
import type { ComercialOverview, ContatoCreditado, DesfechoDaVendaAssistida, ToqueDoMotor, VendaAssistidaInput, VendaAssistidaResposta } from './types';
import { janelaDoToque, LIMITE_DA_REFERENCIA, primeiroErro, temErro, validarVendaAssistida } from './vendaAssistida';

/** GET /api/comercial?from&to -- fila do comercial, vendedoras e resumo. Tenant vem da sessao. */
export function fetchComercial(companyId: string, range: PeriodRange, signal: AbortSignal): Promise<Envelope<ComercialOverview>> {
  return fetchOrSimulate('/api/comercial', rangeQuery(range), signal, () => simularComercial(companyId, range));
}

// -----------------------------------------------------------------------------
// Venda assistida -- POST /api/leads/:leadId/venda-assistida (ADR-001 E5)
// -----------------------------------------------------------------------------

/**
 * O que a previa precisa para decidir como o servidor decidiria. Em modo real
 * e IGNORADO: tenant, taxa e atribuicao sao do servidor (lib/api.ts, decisao 1).
 */
export interface ContextoDaSimulacao {
  readonly companyId: string;
  /** Taxa ATUAL da empresa (com o ajuste da administracao): congela na venda nova. */
  readonly feeBps: number;
  /**
   * Ultimo toque do motor que a tela conhece. `undefined` = a previa procura o
   * lead no conjunto ficticio de leads; `null` = a tela sabe que nao houve toque.
   */
  readonly ultimoToque?: ToqueDoMotor | null;
}

const DESFECHOS: readonly DesfechoDaVendaAssistida[] = ['creditado', 'creditado_revertido', 'sem_contato', 'duplicado'];

function lerContatoCreditado(valor: unknown): ContatoCreditado | null | undefined {
  if (valor === undefined) {
    return undefined;
  }
  if (typeof valor !== 'object' || valor === null) {
    return null;
  }
  const bag = valor as Record<string, unknown>;
  const channel = bag['channel'];
  const touchLabel = bag['touchLabel'];
  const at = bag['at'];
  if ((channel === 'voice' || channel === 'whatsapp' || channel === 'email') && typeof touchLabel === 'string' && typeof at === 'string') {
    return { channel, touchLabel, at };
  }
  return null;
}

/**
 * Confere a resposta em tempo de execucao em vez de confiar num `as`: um
 * backend que mude o contrato vira erro claro ("formato inesperado"), e nao
 * um "creditado" inventado na tela de faturamento.
 */
function lerResposta(bruto: unknown, caminho: string): VendaAssistidaResposta {
  if (typeof bruto === 'object' && bruto !== null) {
    const bag = bruto as Record<string, unknown>;
    const conversion = DESFECHOS.find((d) => d === bag['conversion']);
    const cents = bag['commissionCents'];
    const commissionCents = cents === null || cents === undefined ? null : typeof cents === 'number' && Number.isSafeInteger(cents) && cents >= 0 ? cents : undefined;
    if (conversion !== undefined && commissionCents !== undefined) {
      const creditedTo = lerContatoCreditado(bag['creditedTo']);
      return creditedTo === undefined ? { conversion, commissionCents } : { conversion, commissionCents, creditedTo };
    }
  }
  throw new ApiError('Resposta inesperada ao registrar a venda.', 502, caminho, 'resposta_invalida');
}

/**
 * Registra uma venda fechada pela vendedora sem webhook (Pix manual, link de
 * outra plataforma). O servidor passa a venda pelo MESMO caminho de
 * atribuicao de um `payment_approved` (provedor `manual`) e responde o
 * desfecho. A referencia do pedido e a chave de idempotencia: reenviar a mesma
 * referencia responde `duplicado`, nunca grava duas vezes.
 *
 * Valida antes da rede e lanca `ApiError` 400 (`dados_invalidos`) com a
 * primeira mensagem de campo: nenhum chamador consegue mandar lixo.
 */
export async function registrarVendaAssistida(
  leadId: string,
  dados: VendaAssistidaInput,
  opcoes: { readonly signal?: AbortSignal; readonly simulacao?: ContextoDaSimulacao } = {},
): Promise<VendaAssistidaResposta> {
  const caminho = `/api/leads/${encodeURIComponent(leadId)}/venda-assistida`;
  const erros = validarVendaAssistida(dados, MOCK_FORCED ? AGORA_SIMULADO : new Date());
  if (temErro(erros)) {
    throw new ApiError(primeiroErro(erros) ?? 'Dados da venda inválidos.', 400, caminho, 'dados_invalidos');
  }
  const nota = dados.note?.trim() ?? '';
  const vendedora = dados.sellerId?.trim() ?? '';
  const body: VendaAssistidaInput = {
    amountCents: dados.amountCents,
    orderRef: dados.orderRef,
    ...(dados.paidAt === undefined ? {} : { paidAt: dados.paidAt }),
    ...(nota === '' ? {} : { note: nota }),
    // "Fechado por" viaja junto para o extrato (E5); a simulacao o ignora na atribuicao, como o motor.
    ...(vendedora === '' || vendedora.length > LIMITE_DA_REFERENCIA ? {} : { sellerId: vendedora }),
  };
  if (MOCK_FORCED) {
    return simularVendaAssistida(leadId, body, caminho, opcoes.simulacao, opcoes.signal);
  }
  const bruto = await request<unknown>(caminho, { method: 'POST', body, signal: opcoes.signal });
  return lerResposta(bruto, caminho);
}

// -----------------------------------------------------------------------------
// Simulacao (RNF19) -- deterministica, sem Math.random
// -----------------------------------------------------------------------------

/**
 * Referencias ja registradas nesta aba, por empresa. Imita a idempotencia do
 * servidor: a mesma referencia duas vezes responde `duplicado`. Vive so na
 * memoria da pagina -- recarregar zera, como o resto da previa.
 */
const REGISTRADAS_NA_PREVIA = new Set<string>();

function ultimoToqueDoConjunto(companyId: string, leadId: string, caminho: string): ToqueDoMotor | null {
  const lead = simularDetalheDoLead(companyId, leadId);
  if (lead === null) {
    throw new ApiError('Lead não encontrado nesta empresa.', 404, caminho, 'lead_nao_encontrado');
  }
  const contatos = contatosQueContam(lead.timeline);
  const ultimo = contatos[contatos.length - 1];
  return ultimo === undefined ? null : { at: ultimo.at, rotulo: `${CANAL[ultimo.channel].label} ${ultimo.touchLabel}`.trim() };
}

async function simularVendaAssistida(
  leadId: string,
  dados: VendaAssistidaInput,
  caminho: string,
  contexto: ContextoDaSimulacao | undefined,
  signal: AbortSignal | undefined,
): Promise<VendaAssistidaResposta> {
  // Duas esperas: o botao desabilitado fica visivel, como numa rede de verdade.
  await atrasoSimulado(signal);
  await atrasoSimulado(signal);
  if (signal?.aborted === true) {
    throw new DOMException('Registro cancelado.', 'AbortError');
  }
  if (contexto === undefined) {
    throw new ApiError('Contexto da prévia ausente.', 400, caminho, 'dados_invalidos');
  }
  const toque = contexto.ultimoToque === undefined ? ultimoToqueDoConjunto(contexto.companyId, leadId, caminho) : contexto.ultimoToque;
  const chave = `${contexto.companyId}:${dados.orderRef}`;
  if (REGISTRADAS_NA_PREVIA.has(chave)) {
    return { conversion: 'duplicado', commissionCents: null };
  }
  REGISTRADAS_NA_PREVIA.add(chave);
  const pagoEm = dados.paidAt === undefined ? AGORA_SIMULADO : new Date(dados.paidAt);
  const janela = janelaDoToque(toque, pagoEm, AGORA_SIMULADO);
  if (janela !== null && janela.pagamentoDentro) {
    return { conversion: 'creditado', commissionCents: comissaoSimuladaCents(dados.amountCents, contexto.feeBps) };
  }
  return { conversion: 'sem_contato', commissionCents: null };
}
